/**
 * WHAT THE MUTED SECOND SERIES IS CALLED.
 *
 * Every panel draws one comparison series. What it MEANS depends on which
 * comparison is on — the previous period, or a second building — and the two are
 * mutually exclusive (filter-reducer invariant 9d), so one label describes
 * whichever is active.
 *
 * A CONTEXT rather than a prop threaded through five tabs into fourteen panels.
 * The label is a single string that every panel needs and none of them decides;
 * passing it by hand would mean fourteen call sites to change and, on the day
 * somebody adds a panel, one series quietly captioned "Earlier period" while it
 * is drawing a different building. That is the failure this exists to prevent:
 * a legend that names the wrong thing is worse than no legend, because a reader
 * cannot tell it is wrong.
 *
 * The default is the period case, so a panel rendered outside a provider — in a
 * test, or in the export's own render — reads correctly.
 */
import React, { createContext, useContext, useMemo } from 'react';

export interface CompareMeaning {
  /** Title case, for a legend entry and a series name. */
  label: string;
  /** Lower case, for mid-sentence use in a tooltip row. */
  inline: string;
  /** True when the second series is a PLACE rather than a time. */
  isScope: boolean;
}

const PERIOD: CompareMeaning = {
  label: 'Earlier period',
  inline: 'earlier period',
  isScope: false,
};

const CompareLabelContext = createContext<CompareMeaning>(PERIOD);

export const useCompareMeaning = (): CompareMeaning => useContext(CompareLabelContext);

export const CompareLabelProvider: React.FC<{
  /** The other scope's name. Null or absent means the comparison is the previous
   *  period, which is the default. */
  scopeName?: string | null;
  children: React.ReactNode;
}> = ({ scopeName, children }) => {
  const value = useMemo<CompareMeaning>(
    () => (scopeName ? { label: scopeName, inline: scopeName, isScope: true } : PERIOD),
    [scopeName]
  );

  return (
    <CompareLabelContext.Provider value={value}>{children}</CompareLabelContext.Provider>
  );
};
